import * as React from "react";
import { DataTable, type DataTableColumn } from "./data-table";
import { formatDate, formatDateTime, formatUsd, capitalizeFirst } from "../format";

/** The typed column manifest a saved view carries (mirror of core's ViewColumn). */
export interface SavedViewColumn {
  key: string;
  label: string;
  type: "text" | "number" | "boolean" | "date" | "datetime" | "money" | "urn";
}

export interface SavedViewProps {
  /** The compiled column manifest — drives cell formatting. */
  columns: SavedViewColumn[];
  /** The rows returned by runSavedView (arbitrary shape keyed by column key). */
  rows: Record<string, unknown>[];
  /** Total matching rows (for the count pill), if known. */
  total?: number;
  /** Optional integrated card header title. */
  title?: React.ReactNode;
  /** Row click / nav. */
  onRowClick?: (row: Record<string, unknown>) => void;
  rowHref?: (row: Record<string, unknown>) => string;
  /** Empty-state line. */
  empty?: React.ReactNode;
  /** Pass false when already inside a Card. */
  wrapInCard?: boolean;
}

/** Render one cell value per its column type, through the shared formatters
 *  (money 2dp, dates never with seconds — never inline formatting). */
function renderCell(type: SavedViewColumn["type"], value: unknown): React.ReactNode {
  if (value == null || value === "") return <span className="text-muted-foreground">—</span>;
  switch (type) {
    case "money": {
      // stored as a numeric string / number of USD units.
      const n = typeof value === "number" ? value : Number(value);
      return <span className="tabular-nums">{Number.isFinite(n) ? formatUsd(n) : String(value)}</span>;
    }
    case "number":
      return <span className="tabular-nums">{String(value)}</span>;
    case "date":
      return <span className="text-muted-foreground">{formatDate(String(value))}</span>;
    case "datetime":
      return <span className="text-muted-foreground">{formatDateTime(String(value))}</span>;
    case "boolean":
      return <span className="text-muted-foreground">{value === true || value === "true" ? "Yes" : "No"}</span>;
    case "urn":
      return <span className="font-mono type-subtext text-muted-foreground">{String(value)}</span>;
    default:
      return <span>{String(value)}</span>;
  }
}

/**
 * The shared saved-view renderer (Data Design PRD §6.1). Given a saved view's
 * typed column manifest + its rows (from `runSavedView`), renders a `DataTable`
 * with each cell formatted by its declared column type through the shared
 * formatters. Customized by props (title, row nav, empty state); no hidden
 * global state. Used by /database/views and any surface that pins a view.
 */
export function SavedView({
  columns,
  rows,
  total,
  title,
  onRowClick,
  rowHref,
  empty,
  wrapInCard = true,
}: SavedViewProps) {
  const tableColumns = React.useMemo<DataTableColumn<Record<string, unknown>>[]>(
    () =>
      columns.map((c) => ({
        key: c.key,
        header: capitalizeFirst(c.label),
        align: c.type === "number" || c.type === "money" ? "right" : "left",
        render: (row) => renderCell(c.type, row[c.key]),
      })),
    [columns],
  );

  return (
    <DataTable
      rows={rows}
      columns={tableColumns}
      rowKey={(row, i) => (typeof row.uid === "string" ? row.uid : typeof row.id === "number" ? row.id : i)}
      title={title}
      count={total}
      onRowClick={onRowClick}
      rowHref={rowHref}
      wrapInCard={wrapInCard}
      strongHeader
      empty={empty ?? "No matching records."}
    />
  );
}
